/**
 * Workspace serialization and deserialization logic
 */

import indicatorRegistry from "../../Registry/Indicators/IndicatorRegistry.jsx";
import useWorkspaceStore from "../../store/workspaceStore.jsx";
import { useSidebarFavoritesStore } from "../../store/sidebarFavoritesStore.jsx";
import { useCompareStore } from "../../store/compare";
import useWatchlistStore from "../../store/watchlistStore.jsx";
import useBookByPriceStore from "../../store/bookByPriceStore.jsx";
import { useOrderDefaultsStore } from "../../hooks/useOrderDefaults.js";
import logger from "../../utils/logger";
import { WORKSPACE_CONFIG, createMetadata } from "../constants.js";
import {
  getDefaultAppearance,
  generateRenderKey,
  hasOnlyDefaultAppearance,
} from "./helpers.js";
import {
  serializeAppearance,
  ensureValidAppearance,
} from "../utils/appearanceManager.js";

// Stable empty constants to prevent unnecessary object/array creation
const EMPTY_INDICATORS_ARRAY = [];
const EMPTY_INTERACTIVE_OBJECTS = {};

/**
 * Serialize interactive objects with optimization
 * @param {Object} interactiveObjects - Interactive objects to serialize
 * @returns {Object} - Serialized interactive objects
 */
const serializeInteractiveObjects = (interactiveObjects) => {
  if (!interactiveObjects) return {};

  const serialized = {};
  for (const [key, obj] of Object.entries(interactiveObjects)) {
    // If obj is an array, serialize each item
    if (Array.isArray(obj)) {
      const nonEmptyItems = obj.filter((item) => {
        // Keep items that have meaningful data
        if (!item || typeof item !== "object") return false;

        // Check if it's not just default appearance
        const hasData = Object.keys(item).some((prop) => {
          if (prop === "appearance") return false; // Skip appearance for now
          if (prop === "selected") return item[prop] === true; // Only keep if selected
          if (Array.isArray(item[prop])) return item[prop].length > 0; // Only keep non-empty arrays
          return item[prop] !== undefined && item[prop] !== null;
        });

        // Check appearance separately
        const hasNonDefaultAppearance =
          item.appearance &&
          !hasOnlyDefaultAppearance(item.appearance, null, null);

        return hasData || hasNonDefaultAppearance;
      });

      if (nonEmptyItems.length > 0) {
        serialized[key] = nonEmptyItems.map((item) => {
          const optimized = { ...item };

          // Remove default appearance
          if (
            optimized.appearance &&
            hasOnlyDefaultAppearance(optimized.appearance, null, null)
          ) {
            delete optimized.appearance;
          } else if (optimized.appearance) {
            optimized.appearance = serializeAppearance(optimized.appearance);
          }

          return optimized;
        });
      }
    } else {
      // For non-array objects, check if it has any meaningful data
      const hasData = Object.keys(obj).some((prop) => {
        if (prop === "appearance") return false;
        if (Array.isArray(obj[prop])) return obj[prop].length > 0;
        return obj[prop] !== undefined && obj[prop] !== null;
      });

      const hasNonDefaultAppearance =
        obj.appearance && !hasOnlyDefaultAppearance(obj.appearance, null, null);

      if (hasData || hasNonDefaultAppearance) {
        const optimized = { ...obj };

        // Remove default appearance
        if (
          optimized.appearance &&
          hasOnlyDefaultAppearance(optimized.appearance, null, null)
        ) {
          delete optimized.appearance;
        } else if (optimized.appearance) {
          optimized.appearance = serializeAppearance(optimized.appearance);
        }

        serialized[key] = optimized;
      }
    }
  }
  return serialized;
};

/**
 * Serialize indicators with optimization
 * @param {Array} indicators - Indicators to serialize
 * @returns {Array} - Serialized indicators
 */
export const serializeIndicators = (indicators) => {
  if (!indicators) return EMPTY_INDICATORS_ARRAY;

  return indicators.map((indicator) => {
    const optimized = {
      key: indicator.key,
      id: indicator.id,
      config: indicator.config || {},
      // Always save appearanceConfig to ensure proper restoration
      appearanceConfig: serializeAppearance(indicator.appearanceConfig || {}),
    };

    // Only include renderKey if it's different from auto-generated
    const autoGeneratedRenderKey = generateRenderKey(
      indicator.key,
      indicator.config,
    );
    if (indicator.renderKey && indicator.renderKey !== autoGeneratedRenderKey) {
      optimized.renderKey = indicator.renderKey;
    }

    return optimized;
  });
};

/**
 * Get workspace layout state from stores
 * @returns {Object} - Workspace layout configuration
 */
const getWorkspaceLayout = () => {
  const workspaceState = useWorkspaceStore.getState();
  const sidebarState = useSidebarFavoritesStore.getState();
  const bookByPriceState = useBookByPriceStore.getState();
  const bookByPriceFallback = {
    symbol: bookByPriceState.symbol,
    group: bookByPriceState.group,
    viewMode: bookByPriceState.viewMode,
  };

  const enrichDockLayout = (node) => {
    if (!node || typeof node !== "object") return node;
    const children = Array.isArray(node.children)
      ? node.children.map(enrichDockLayout)
      : node.children;
    const withChildren = (nextNode) =>
      children === undefined ? nextNode : { ...nextNode, children };

    if (node.type === "tab" && node.component === "bookByPrice") {
      const instanceState =
        bookByPriceState.instances[node.id] ||
        node.config?.bookByPriceState ||
        bookByPriceFallback;
      return withChildren({
        ...node,
        config: {
          ...(node.config || {}),
          bookByPriceState: {
            symbol: instanceState.symbol ?? bookByPriceFallback.symbol,
            group: instanceState.group ?? null,
            viewMode: instanceState.viewMode ?? bookByPriceFallback.viewMode,
          },
        },
      });
    }

    return children === node.children ? node : { ...node, children };
  };

  // Read the LIVE FlexLayout model when available: popout window moves and
  // resizes never reach the persisted dockLayout (no model action fires), but
  // model.toJson() re-reads each window's current screen rect.
  const dockLayout = workspaceState.getLiveDockLayout
    ? workspaceState.getLiveDockLayout()
    : workspaceState.dockLayout;

  return {
    panels: {
      routerOperationsSidebar: {
        open: workspaceState.isRouterOperationsSidebarOpen,
      },
      objectsSidebar: {
        open: sidebarState.sidebarOpen,
      },
    },
    dockLayout: dockLayout ? enrichDockLayout(dockLayout) : null,
  };
};

/**
 * Create a workspace snapshot from chart store state
 * @param {Object} chartsStore - The charts store state
 * @param {Object} options - Serialization options
 * @param {boolean} options.simplified - Create simplified snapshot (for auto-save)
 * @param {Object} options.metadata - Optional custom metadata
 * @returns {Object} - Workspace snapshot
 */
export const createWorkspaceSnapshot = (chartsStore, options = {}) => {
  const { simplified = false, metadata } = options;
  const { charts, selectedTab, activeInteractiveTools } = chartsStore;

  // Get desktopName from workspaceStore
  const desktopName =
    useWorkspaceStore.getState().desktopName || "Novo desktop";

  // Get comparison symbols from compare store
  const compareState = useCompareStore.getState();

  // Create serialized charts (same logic for both simplified and full)
  const serializedCharts = charts.map((chart) => {
    // Get comparison symbols for this chart (only save symbol and color, not data)
    const chartComparison = compareState.comparisons[chart.id];
    const comparisonSymbols =
      chartComparison?.symbols?.map((s) => ({
        symbol: s.symbol,
        color: s.color,
      })) || [];

    return {
      id: chart.id,
      symbol: chart.symbol,
      periodicity: chart.periodicity,
      chartType: chart.chartType,
      indicators: simplified
        ? chart.indicators || []
        : serializeIndicators(chart.indicators),
      showGrid: chart.showGrid,
      interactiveObjects: simplified
        ? chart.interactiveObjects || {}
        : serializeInteractiveObjects(chart.interactiveObjects),
      visibleBars: chart.visibleBars,
      xExtents: chart.xExtents,
      // Anchor xExtents (an absolute index domain) to the newest bar at save
      // time, from the live data length, so the viewport can be re-anchored to
      // the last bar after a reload refetches a fixed-size window (prevents the
      // 'chart drifts left after refresh' bug). See rebaseXExtentsForReplace.
      xExtentsLastIndex: (() => {
        const liveLen = chartsStore.chartData?.[chart.id]?.data?.length;
        if (typeof liveLen === "number" && liveLen > 0) return liveLen - 1;
        return typeof chart.xExtentsLastIndex === "number"
          ? chart.xExtentsLastIndex
          : null;
      })(),
      sectionHeights: chart.sectionHeights,
      sectionOrder: chart.sectionOrder,
      // Include aggregation chart configs (Renko, Point & Figure)
      renkoConfig: chart.renkoConfig,
      pointFigureConfig: chart.pointFigureConfig,
      barColorProvider: chart.barColorProvider,
      barColorConfig: chart.barColorConfig,
      // Include comparison symbols
      comparisonSymbols:
        comparisonSymbols.length > 0 ? comparisonSymbols : undefined,
      // Include linking group
      group: chart.group,
    };
  });

  // Get watchlist data from watchlist store (always include - part of desktop)
  const watchlistState = useWatchlistStore.getState();
  const watchlistGroup = watchlistState.group;
  const watchlistData = {
    watchlists: watchlistState.watchlists,
    activeWatchlist: watchlistState.activeWatchlist,
    columnConfigs: watchlistState.columnConfigs,
  };
  const bookByPriceState = useBookByPriceStore.getState();
  const bookByPriceData = {
    symbol: bookByPriceState.symbol,
    group: bookByPriceState.group,
    viewMode: bookByPriceState.viewMode,
  };

  // Per-symbol order defaults (quantity + OTO config) — part of the desktop
  const orderDefaultsData = useOrderDefaultsStore.getState().serialize();

  return {
    version: WORKSPACE_CONFIG.CURRENT_VERSION,
    timestamp: new Date().toISOString(),
    desktopName, // Include desktop name in snapshot
    charts: serializedCharts,
    selectedTab,
    activeInteractiveTools: activeInteractiveTools || {},
    workspaceLayout: getWorkspaceLayout(),
    watchlistGroup, // Include watchlist linking group
    watchlistData, // Include watchlist symbols and order (only for full saves)
    bookByPriceData,
    orderDefaultsData, // Per-symbol quantity + OTO config
    metadata:
      metadata ||
      createMetadata(
        simplified
          ? "Auto-saved desktop snapshot"
          : "Desktop configuration with layout",
        simplified,
      ),
  };
};

/**
 * Serialize the current chart store state for saving
 * @param {Object} chartsStore - The charts store state
 * @returns {Promise<Object>} - Serialized desktop data
 */
export const serializeWorkspace = async (chartsStore) => {
  return createWorkspaceSnapshot(chartsStore, { simplified: false });
};

/**
 * Ensure interactive objects have valid appearance
 * @param {Object} interactiveObjects - Interactive objects to validate
 * @returns {Object} - Validated interactive objects
 */
const ensureValidInteractiveObjects = (interactiveObjects) => {
  if (!interactiveObjects) return EMPTY_INTERACTIVE_OBJECTS;

  const validated = {};
  for (const [key, obj] of Object.entries(interactiveObjects)) {
    // Ensure the container has a valid appearance
    const containerAppearance = ensureValidAppearance(obj.appearance);

    // If obj is an array, ensure each item has a valid appearance
    if (Array.isArray(obj)) {
      validated[key] = obj.map((item) => {
        if (item && typeof item === "object") {
          return {
            ...item,
            appearance: ensureValidAppearance(item.appearance),
          };
        }
        return item;
      });
    } else {
      // For non-array objects, just ensure the container appearance
      validated[key] = {
        ...obj,
        appearance: containerAppearance,
      };
    }
  }
  return validated;
};

/**
 * Migrate MACD indicator appearance from old format to new format
 * Old format: { barDivergence: { fillColor: "#165294" } }
 * New format: { barDivergence: { increasingColor: "#00DD00", decreasingColor: "#FF0000" } }
 * @param {string} indicatorKey - Indicator key
 * @param {Object} appearanceConfig - Appearance configuration
 * @returns {Object} - Migrated appearance configuration
 */
const migrateIndicatorAppearance = (indicatorKey, appearanceConfig) => {
  if (!appearanceConfig) return appearanceConfig;

  // Migrate MACD barDivergence from old format (fillColor) to new format (increasingColor/decreasingColor)
  if (indicatorKey === "macd" && appearanceConfig.barDivergence) {
    const { barDivergence } = appearanceConfig;

    // If already has new format, no migration needed
    if (barDivergence.increasingColor || barDivergence.decreasingColor) {
      return appearanceConfig;
    }

    // If has old format (fillColor), migrate to new format
    if (barDivergence.fillColor) {
      console.log(
        "[Migration] Migrating MACD barDivergence from old format to new format",
      );
      return {
        ...appearanceConfig,
        barDivergence: {
          // Preserve width and opacity
          width: barDivergence.width ?? 1,
          opacity: barDivergence.opacity ?? 0.6,
          // Use ChartIQ CSS colors: .stx_histogram_up (#8cc176) and .stx_histogram_down (#b82c0c)
          increasingColor: "#8cc176",
          decreasingColor: "#b82c0c",
        },
      };
    }
  }

  return appearanceConfig;
};

/**
 * Ensure indicators have valid appearance and configuration
 * @param {Array} indicators - Indicators to validate
 * @returns {Array} - Validated indicators
 */
export const ensureValidIndicators = (indicators) => {
  if (!indicators) return EMPTY_INDICATORS_ARRAY;

  return indicators.map((indicator) => {
    // Find the indicator in the registry to restore the type function
    let registryIndicator = null;
    for (const categoryKey of Object.keys(indicatorRegistry)) {
      const category = indicatorRegistry[categoryKey];
      if (category.indicators && category.indicators[indicator.key]) {
        registryIndicator = category.indicators[indicator.key];
        break;
      }
    }

    // Get default appearance from registry
    const defaultAppearance = registryIndicator
      ? getDefaultAppearance(indicator.key, registryIndicator.categoryKey || "")
      : {};

    // Merge and validate appearanceConfig
    let appearanceConfig = indicator.appearanceConfig || {};

    // Apply migrations for backward compatibility
    appearanceConfig = migrateIndicatorAppearance(
      indicator.key,
      appearanceConfig,
    );

    // Merge with defaults
    appearanceConfig = { ...defaultAppearance, ...appearanceConfig };

    // Ensure valid appearance structure (stroke, fill, etc.)
    appearanceConfig = ensureValidAppearance(appearanceConfig);

    // Merge with registry data if found, otherwise keep as is
    const enrichedIndicator = registryIndicator
      ? {
          ...registryIndicator,
          ...indicator,
          // Ensure config and appearanceConfig are preserved
          config: indicator.config || {},
          appearanceConfig: appearanceConfig,
          // Generate renderKey if not provided
          renderKey:
            indicator.renderKey ||
            generateRenderKey(indicator.key, indicator.config || {}),
        }
      : {
          ...indicator,
          appearanceConfig: appearanceConfig,
          config: indicator.config || {},
          renderKey:
            indicator.renderKey ||
            generateRenderKey(indicator.key, indicator.config || {}),
        };

    // Debug logging
    if (!registryIndicator) {
      console.warn("⚠️ Indicator not found in registry:", indicator.key);
    }

    return enrichedIndicator;
  });
};

/**
 * Restore workspace layout state
 * @param {Object} layout - Workspace layout configuration
 * @param {Object} [options]
 * @param {boolean} [options.resetDock] - monta o dock do zero quando o
 *   workspace não traz layout (ver #438 abaixo)
 */
const restoreWorkspaceLayout = (layout, { resetDock = false } = {}) => {
  if (!layout || !layout.panels) {
    // #438: "sem workspaceLayout" era um sinal ambíguo. O conversor do
    // WebCharts antigo omite o campo de PROPÓSITO, para preservar os painéis do
    // usuário (legacyConverter.js: "preserve user's current panel states"); o
    // conversor Silverlight omite pelo motivo OPOSTO — quer o dock montado do
    // zero, com grid/abas aplicado depois pelo requestOrganize. Sem distinguir
    // os dois, a importação do Silverlight herdava watchlist/livro/DoM do
    // desktop anterior. Quem quer o dock limpo agora pede explicitamente.
    if (resetDock) {
      const store = useWorkspaceStore.getState();
      store.setDockLayout(null);
      store.incrementDockLayoutKey();
      console.log("No workspace layout data found, dock reset requested");
      return;
    }
    console.log("No workspace layout data found, using defaults");
    return;
  }

  const workspaceStore = useWorkspaceStore.getState();

  console.log("Restoring workspace layout...");

  // Restore panel visibility states
  workspaceStore.setRouterOperationsSidebarOpen(
    layout.panels.routerOperationsSidebar?.open ?? true,
  );

  // Restore ObjectsSidebar visibility
  if (layout.panels.objectsSidebar?.open !== undefined) {
    const sidebarStore = useSidebarFavoritesStore.getState();
    sidebarStore.setSidebarOpen(layout.panels.objectsSidebar.open);
  }

  // Restore dock layout if available
  if (layout?.dockLayout) {
    workspaceStore.setDockLayout(layout.dockLayout);
  } else {
    // Legacy desktop (no dockLayout) — clear any stale dock layout from
    // a previously loaded desktop, and pass panel hints so DockModeLayout
    // can build a layout that mirrors the old panel positions
    workspaceStore.setDockLayout(null);
    // Must use setState (not direct mutation) because setDockLayout() above
    // created a new Zustand state object, making the captured ref stale
    useWorkspaceStore.setState({
      _legacyPanelHints: {
        routerInfoPanel: layout.panels.routerInfoPanel?.open ?? false,
        logPanel: layout.panels.logPanel?.open ?? false,
        tabMode: true, // Legacy desktops used tabs, not grid
      },
    });
  }
  // Increment key to force DockModeLayout remount with new dockLayout
  workspaceStore.incrementDockLayoutKey();

  console.log("Workspace layout restored successfully");
};

/**
 * Deserializes desktop data and loads it into the store
 * @param {Object} desktopData - The serialized desktop data
 * @param {Function} loadCharts - Function to load charts into store
 * @param {Object} [options]
 * @param {boolean} [options.resetDock] - descarta o dock do desktop anterior
 *   quando o workspace não traz layout próprio (#438). Só quem monta o dock do
 *   zero pede isso — o import do WebCharts antigo depende do contrário.
 * @returns {boolean} - Success status
 */
export const deserializeWorkspace = (desktopData, loadCharts, options = {}) => {
  try {
    console.log("Deserializing desktop data...");
    console.log("Desktop data keys:", Object.keys(desktopData || {}));

    if (!desktopData || !desktopData.charts) {
      console.error("Invalid desktop data: missing charts");
      logger.workspace.error("Desktop inválido: dados de gráficos ausentes");
      throw new Error("Invalid desktop data");
    }

    console.log("Charts to load:", desktopData.charts.length);

    // Handle version differences for backward compatibility
    const version = desktopData.version || WORKSPACE_CONFIG.LEGACY_VERSION;
    if (version === WORKSPACE_CONFIG.LEGACY_VERSION) {
      console.log("Loading legacy desktop (v1.0) - using default layout");
    } else if (version === "1.2") {
      console.log("Loading desktop with dock layout support (v1.2)");
    } else if (
      version === "1.1" ||
      version === WORKSPACE_CONFIG.CURRENT_VERSION
    ) {
      console.log("Loading desktop with layout support (v1.1)");
    } else {
      console.warn(`Unknown desktop version: ${version}, treating as legacy`);
    }

    // Validate and fix charts data
    const validatedCharts = desktopData.charts.map((chart) => ({
      ...chart,
      indicators: ensureValidIndicators(chart.indicators),
      interactiveObjects: ensureValidInteractiveObjects(
        chart.interactiveObjects,
      ),
      // Ensure sectionOrder exists for backward compatibility
      sectionOrder: chart.sectionOrder || [0, 1], // Default: main chart first, volume second
      // Preserve null sectionHeights (for recalculation), only provide defaults for undefined
      sectionHeights:
        chart.sectionHeights !== undefined
          ? chart.sectionHeights
          : { 0: 0.75, 1: 0.25 },
      // Ensure aggregation chart configs exist for backward compatibility
      renkoConfig: chart.renkoConfig || { brickSize: null },
      pointFigureConfig: chart.pointFigureConfig || {
        boxSize: null,
        reversal: 3,
      },
      // Ensure bar color settings exist
      barColorProvider: chart.barColorProvider || "traditional",
      barColorConfig: chart.barColorConfig || {},
      // Restore linking group (null if not set)
      group: chart.group || null,
    }));

    console.log(
      "[Deserialize] Validated charts:",
      validatedCharts.map((c) => ({
        symbol: c.symbol,
        periodicity: c.periodicity,
        indicators: c.indicators?.length || 0,
        interactiveObjects: Object.keys(c.interactiveObjects || {}).length,
      })),
    );
    console.log("[Deserialize] Calling loadCharts...");

    // Load the charts data
    loadCharts(validatedCharts, desktopData.selectedTab);

    console.log("[Deserialize] loadCharts called successfully");

    // Restore comparison symbols for each chart (async, after charts are loaded)
    const chartsWithComparisons = validatedCharts.filter(
      (c) => c.comparisonSymbols?.length > 0,
    );
    if (chartsWithComparisons.length > 0) {
      console.log(
        "[Deserialize] Restoring comparison symbols for",
        chartsWithComparisons.length,
        "charts",
      );

      // Use setTimeout to ensure charts are fully loaded before adding comparison symbols
      setTimeout(async () => {
        const compareStore = useCompareStore.getState();
        for (const chart of chartsWithComparisons) {
          console.log(
            `[Deserialize] Adding ${chart.comparisonSymbols.length} comparison symbols to chart ${chart.id}`,
          );
          for (const { symbol, color } of chart.comparisonSymbols) {
            // Add comparison symbol with specific color
            await compareStore.addComparisonSymbol(
              chart.id,
              symbol,
              { periodicity: chart.periodicity },
              color,
            );
          }
        }
        console.log("[Deserialize] Comparison symbols restored");
      }, 500);
    }

    // Restore workspace layout if available
    restoreWorkspaceLayout(desktopData.workspaceLayout, {
      resetDock: options.resetDock === true,
    });

    // Restore desktop name if available (important for auto-save restore)
    if (desktopData.desktopName) {
      useWorkspaceStore.getState().setDesktopName(desktopData.desktopName);
      console.log("Restored desktop name:", desktopData.desktopName);
    }

    // Reset watchlists before restoring — ensures clean slate when loading
    // desktops that have no watchlist data (old desktops) or different data.
    useWatchlistStore.getState().resetWatchlists();

    // Restore watchlist linking group if available
    if (desktopData.watchlistGroup !== undefined) {
      useWatchlistStore.getState().setGroup(desktopData.watchlistGroup);
      console.log("Restored watchlist group:", desktopData.watchlistGroup);
    }

    // Restore watchlist data if available (symbols, order, active watchlist)
    if (desktopData.watchlistData) {
      const { watchlists, activeWatchlist } = desktopData.watchlistData;

      if (watchlists && Object.keys(watchlists).length > 0) {
        useWatchlistStore.setState({
          watchlists,
          activeWatchlist: activeWatchlist || Object.keys(watchlists)[0],
          columnConfigs: desktopData.watchlistData.columnConfigs || {},
        });
        console.log(
          "Restored watchlist data:",
          Object.keys(watchlists).length,
          "lists",
        );
      }
    }

    useBookByPriceStore.getState().reset();
    if (desktopData.bookByPriceData) {
      useBookByPriceStore.getState().hydrate(desktopData.bookByPriceData);
      console.log("Restored BookByPrice data:", desktopData.bookByPriceData);
    }

    // Restore per-symbol order defaults (quantity + OTO config).
    // Runs after loadCharts so hydrated values win over setActiveChart defaults.
    useOrderDefaultsStore.getState().reset();
    if (desktopData.orderDefaultsData) {
      useOrderDefaultsStore.getState().hydrate(desktopData.orderDefaultsData);
      console.log("Restored order defaults:", desktopData.orderDefaultsData);
    }

    console.log("Desktop deserialization completed successfully");

    // Success logged by caller with more specific context (e.g., desktop name)
    return true;
  } catch (error) {
    console.error("Failed to deserialize desktop:", error);

    // Log de erro para o usuário
    logger.workspace.error("Falha ao carregar desktop", {
      error: error.message,
      details: {
        hasCharts: !!desktopData?.charts,
        chartsCount: desktopData?.charts?.length || 0,
      },
    });

    return false;
  }
};
